import { Button } from '@nautilusbio/nb-design-system/Button';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useForm } from '@tanstack/react-form';
import { UseMutationResult } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { PencilLineIcon } from 'lucide-react';
import React, { type FC, useCallback, useState } from 'react';

import type { MutationContext, UpdateVariables } from '../../hooks';
import type { InstrumentRunPublic } from '../../types';

import styles from './styles.module.css';
import DescriptionEditForm from './subcomponents/DescriptionEditForm';
import DescriptionModal from './subcomponents/DescriptionModal';
import DescriptionPreview from './subcomponents/DescriptionPreview';

export type TitlePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface DescriptionProps {
  /** The title/label to display */
  title: string;
  /** The body text content */
  body: string;
  /** Position of the title label */
  titlePosition?: TitlePosition;
  /** Whether the component is editable */
  canEdit?: boolean;
  /** Field name for the form field */
  fieldName?: string;
  /** TanStack Query mutation hook result from parent */
  mutation?: UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext>;
  /** Custom className for the container */
  className?: string;
  /** Aria label for the edit button */
  'aria-label'?: string;
}

const MAX_HEIGHT = 260; // px

const Description: FC<DescriptionProps> = ({
  title,
  body,
  titlePosition = 'top-left',
  canEdit = false,
  fieldName = 'description',
  mutation,
  className,
  'aria-label': ariaLabel = 'Edit description',
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm({
    defaultValues: {
      [fieldName]: body || '',
    },
    onSubmit: async ({ value }) => {
      if (mutation) {
        // Convert the form value shape { [fieldName]: string } into the
        // strongly-typed UpdateVariables expected by the mutation hook.
        const variables: UpdateVariables = {
          description: String(value[fieldName] ?? ''),
        };
        await mutation.mutateAsync(variables);
        setIsEditing(false);
        // Keep form values in sync with the saved result so subsequent edits start from the latest text
        form.reset({ [fieldName]: variables.description });
      } else {
        setIsEditing(false);
      }
    },
  });

  // Cancel handler for edit form - ensure reset to the latest `body`
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    // Restore the form value to the current prop so a cancelled edit doesn't show stale data next time
    form.reset({ [fieldName]: body || '' });
  }, [body, fieldName, form]);

  const handleStartEdit = useCallback(() => {
    // Initialize form values from the latest body before entering edit mode
    form.reset({ [fieldName]: body || '' });
    setIsEditing(true);
  }, [body, fieldName, form]);

  return (
    <div className={clsx(styles.wrapper, className)}>
      {isEditing && canEdit ? (
        <DescriptionEditForm
          form={form}
          fieldName={fieldName}
          title={title}
          mutation={mutation}
          ariaLabel={ariaLabel}
          onCancel={handleCancelEdit}
        />
      ) : (
        <div className={clsx(styles.container)}>
          <div className={styles.content}>
            <DescriptionPreview
              body={body}
              title={title}
              titlePosition={titlePosition}
              maxHeight={MAX_HEIGHT}
              className={styles.bodyPreview}
            >
              <DescriptionModal title={title} canEdit={canEdit} body={body} buttonClassName={styles.fullscreenBtn}>
                {canEdit && (
                  <DescriptionEditForm
                    form={form}
                    fieldName={fieldName}
                    title={title}
                    mutation={mutation}
                    ariaLabel={ariaLabel}
                    onCancel={handleCancelEdit}
                    fullscreen
                  />
                )}
              </DescriptionModal>
            </DescriptionPreview>
          </div>

          {canEdit && (
            <Tooltip
              content={'Edit the description'}
              placement="bottom-end"
              trigger={
                <Button
                  aria-label={ariaLabel}
                  className={styles.editBtn}
                  data-testid={`${fieldName}-edit-btn`}
                  onPress={handleStartEdit}
                  size="xs"
                  visual="unstyled"
                >
                  <PencilLineIcon size={18} />
                </Button>
              }
              triggerClassName={styles.tooltipTrigger}
            />
          )}
        </div>
      )}
    </div>
  );
};

Description.displayName = 'Description';

export default Description;
